import { fetchStripeAccountLinks, fetchUser } from "actions/user";
import RequireData from "hooks/RequireData";
import SageButton from "kit/SageButton";
import React from "react";
import fetchUserStatusSelector from "selectors/fetchUserStatus";
import userSelector from "selectors/user";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { createTransfer, setCreateTransferStatus } from "actions/balance";
import createTransferStatusSelector from "selectors/createTransferStatus";
import SageErrorAlert from "kit/SageErrorAlert";
import stripeAccountLinkStatusSelector from "selectors/stripeAccountLinkStatus";
import stripeAccountLinkSelector from "selectors/stripeAccountLink";
import BalanceMoneyCreateTransferConnect from "./BalanceMoneyCreateTransferConnect";
import stringDollarToCentHundredths from "utils/stringDollarToCentHundredths";

const BalanceMoneyCreateTransferForm: React.FC = () => {
    const dispatch = useDispatch();
    const [value, setValue] = React.useState("");
    const [isInvaid, setIsInvalid] = React.useState(false);
    const createTransferStatus = useSelector(createTransferStatusSelector);

    const [user] = RequireData(
        userSelector,
        fetchUserStatusSelector,
        fetchUser()
    );

    const setToFullAmount = React.useCallback(
        (e) => {
            e.preventDefault();
            if (!user) {
                return;
            }
            const valInDollars =
                Math.floor(user.money_balance_cent_hundredths / 100) / 100;
            setValue(valInDollars.toFixed(2));
        },
        [user]
    );

    const onErrorDismiss = React.useCallback(() => {
        dispatch(setCreateTransferStatus("none"));
    }, [dispatch]);

    const onInputChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
        },
        [setValue]
    );

    const errorText = React.useMemo(() => {
        if (createTransferStatus === "error") {
            return "An unknown error occurred when creating your transfer.";
        } else if (createTransferStatus === "insufficient_funds") {
            return "Your don't have as much money in your account that you transferring.";
        } else if (createTransferStatus === "request_parse_error") {
            return "Invalid amount, please try again.";
        } else {
            return null;
        }
    }, [createTransferStatus]);

    const handleSubmit = React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const centHundredths = stringDollarToCentHundredths(value);
            if (isNaN(centHundredths)) {
                setIsInvalid(true);
                return;
            }

            setIsInvalid(false);

            dispatch(createTransfer(centHundredths));
        },
        [setIsInvalid, value, dispatch]
    );

    const stripeAccountLinkStatus = useSelector(
        stripeAccountLinkStatusSelector
    );
    const stripeAccountLink = useSelector(stripeAccountLinkSelector);

    React.useEffect(() => {
        dispatch(fetchStripeAccountLinks());
    }, [dispatch]);

    if (stripeAccountLinkStatus === "loading") {
        return <p>Loading...</p>;
    }

    if (stripeAccountLinkStatus === "error") {
        return <p>An error occurred. Please refresh to try again.</p>;
    }

    if (stripeAccountLink) {
        return (
            <BalanceMoneyCreateTransferConnect
                accountLink={stripeAccountLink}
            />
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label htmlFor="money" className="form-label">
                        Money to Transfer
                    </label>
                    <div className="input-group mb-2">
                        <div className="input-group-prepend">
                            <div className="input-group-text">$</div>
                        </div>
                        <input
                            onChange={onInputChanged}
                            value={value}
                            type="text"
                            id="money"
                            className={classnames(
                                "form-control",
                                isInvaid && "is-invalid"
                            )}
                        />
                        <div className="invalid-feedback">
                            Please input the amount that you would like to
                            transfer in the form 54.23 (maximum two decimal
                            points)
                        </div>
                    </div>
                    {user != null && (
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={setToFullAmount}
                        >
                            Transfer All
                        </button>
                    )}
                </div>
                <SageButton
                    loading={createTransferStatus === "loading"}
                    type="submit"
                    text="Submit"
                />
            </form>
            {errorText && (
                <SageErrorAlert
                    text={errorText || undefined}
                    onDismiss={onErrorDismiss}
                />
            )}
        </>
    );
};

export default BalanceMoneyCreateTransferForm;
